import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, ThumbsUp, MessageCircle, Filter, ChevronDown, User, CheckCircle, Plus, X } from 'lucide-react';
import styled from 'styled-components';
import reviewService from '../services/reviewService';

// Palette de couleurs INZA STORE
const INZA_COLORS = {
  primary: '#FF6B35',
  secondary: '#F7931E',
  dark: '#2C3E50',
  light: '#ECF0F1',
  accent: '#E74C3C'
};

// Styled Components
const Section = styled.section`
  padding: 5rem 1rem;
  background: #ffffff;
  color: #1f2937;
  
  @media (min-width: 768px) {
    padding: 5rem 2rem;
  }
  
  @media (min-width: 1024px) {
    padding: 5rem 4rem;
  }
`;

const Container = styled.div`
  max-width: 80rem;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  
  h2 {
    font-size: clamp(2.5rem, 5vw, 3.5rem);
    font-weight: 700;
    margin-bottom: 1.5rem;
    background: linear-gradient(135deg, ${INZA_COLORS.primary}, ${INZA_COLORS.secondary});
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  p {
    font-size: clamp(1.1rem, 2vw, 1.3rem);
    color: #6b7280;
    max-width: 48rem;
    margin: 0 auto;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
  
  .stat-card {
    text-align: center;
    padding: 2rem;
    background: white;
    border-radius: 1rem;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
    
    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
    }
    
    .rating-display {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      margin-bottom: 1rem;
      
      .stars {
        display: flex;
        
        .star {
          width: 1.25rem;
          height: 1.25rem;
          color: #fbbf24;
          fill: currentColor;
        }
      }
      
      .rating-number {
        font-size: 2rem;
        font-weight: 700;
        color: #1f2937;
      }
    }
    
    .total-reviews {
      color: #6b7280;
      font-size: 0.875rem;
    }
  }
`;

const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 3rem;
  
  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
  
  .filter-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    
    .filter-btn {
      padding: 0.5rem 1rem;
      border-radius: 9999px;
      border: 2px solid #e5e7eb;
      background: white;
      color: #6b7280;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s ease;
      
      &:hover {
        border-color: ${INZA_COLORS.primary};
        color: ${INZA_COLORS.primary};
      }
      
      &.active {
        background: ${INZA_COLORS.primary};
        border-color: ${INZA_COLORS.primary};
        color: white;
      }
      
      .count {
        background: rgba(255, 107, 53, 0.1);
        color: ${INZA_COLORS.primary};
        padding: 0.125rem 0.5rem;
        border-radius: 9999px;
        font-size: 0.75rem;
        margin-left: 0.5rem;
        
        .active & {
          background: rgba(255, 255, 255, 0.2);
          color: white;
        }
      }
    }
  }
  
  .sort-dropdown {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    select {
      padding: 0.5rem 1rem;
      border-radius: 0.5rem;
      border: 1px solid #e5e7eb;
      background: white;
      outline: none;
      
      &:focus {
        border-color: ${INZA_COLORS.primary};
        box-shadow: 0 0 0 2px rgba(255, 107, 53, 0.2);
      }
    }
  }
  
  .model-filters {
    h4 {
      font-size: 0.9rem;
      font-weight: 600;
      color: #374151;
      margin-bottom: 0.75rem;
    }
  }
`;

const ReviewsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  margin-bottom: 3rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ReviewCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  padding: 1.5rem;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    border-color: #3b82f6;
  }
  
  .review-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;
    
    .author-info {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      
      .avatar {
        width: 40px;
        height: 40px;
        background: linear-gradient(135deg, #3b82f6, #9333ea);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: 600;
        font-size: 1rem;
      }
      
      .author-details {
        .author-name {
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 0.25rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.95rem;
        }
        
        .review-date {
          font-size: 0.85rem;
          color: #6b7280;
        }
      }
    }
    
    .rating {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      
      .stars {
        display: flex;
        gap: 0.125rem;
        
        .star {
          color: #fbbf24;
          font-size: 1rem;
        }
      }
    }
  }
  
  .review-content {
    margin-bottom: 1rem;
    flex: 1;
    
    .review-title {
      font-size: 1.1rem;
      font-weight: 600;
      color: #1f2937;
      margin-bottom: 0.75rem;
      line-height: 1.3;
    }
    
    .review-text {
      color: #4b5563;
      line-height: 1.5;
      font-size: 0.9rem;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  
  .review-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 1rem;
    border-top: 1px solid #f3f4f6;
    margin-top: auto;
    
    .helpful-btn {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 0.5rem;
      padding: 0.5rem 0.75rem;
      color: #64748b;
      font-size: 0.85rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;
      
      &:hover {
        background: #3b82f6;
        border-color: #3b82f6;
        color: white;
        transform: translateY(-1px);
      }
      
      &.liked {
        background: #3b82f6;
        border-color: #3b82f6;
        color: white;
      }
    }
    
    .reply-btn {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 0.5rem;
      padding: 0.5rem 0.75rem;
      color: #64748b;
      font-size: 0.85rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;
      
      &:hover {
        background: #10b981;
        border-color: #10b981;
        color: white;
        transform: translateY(-1px);
      }
    }
  }
`;

const ShowMoreButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 3rem auto 0;
  background: linear-gradient(135deg, #3b82f6, #9333ea);
  border: none;
  border-radius: 9999px;
  padding: 1rem 2rem;
  color: white;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(59, 130, 246, 0.3);
  }
`;

const ReplyForm = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  
  .reply-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;
    
    .reply-title {
      font-weight: 600;
      color: #1f2937;
      font-size: 0.9rem;
    }
    
    .cancel-btn {
      background: none;
      border: none;
      color: #6b7280;
      cursor: pointer;
      padding: 0.25rem;
      border-radius: 0.25rem;
      
      &:hover {
        background: #e5e7eb;
        color: #374151;
      }
    }
  }
  
  .reply-textarea {
    width: 100%;
    min-height: 100px;
    padding: 0.75rem;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    font-family: inherit;
    font-size: 0.875rem;
    resize: vertical;
    outline: none;
    
    &:focus {
      border-color: #3b82f6;
      box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
    }
    
    &::placeholder {
      color: #9ca3af;
    }
  }
  
  .reply-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 1rem;
    
    .char-count {
      font-size: 0.75rem;
      color: #6b7280;
    }
    
    .submit-btn {
      background: linear-gradient(135deg, #3b82f6, #9333ea);
      border: none;
      border-radius: 0.5rem;
      padding: 0.5rem 1.5rem;
      color: white;
      font-weight: 600;
      font-size: 0.875rem;
      cursor: pointer;
      transition: all 0.2s ease;
      
      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
      }
      
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none;
      }
    }
  }
`;

const ReviewsSection = () => {
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedModel, setSelectedModel] = useState('all');
  const [expandedReview, setExpandedReview] = useState(null);
  const [showMore, setShowMore] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replies, setReplies] = useState({});
  const [likedReviews, setLikedReviews] = useState(new Set());
  const [likedReplies, setLikedReplies] = useState(new Set());
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Charger les avis depuis l'API
  useEffect(() => {
    const loadReviews = async () => {
      try {
        setLoading(true);
        const [reviewsData, statsData] = await Promise.all([
          reviewService.getReviews(),
          reviewService.getReviewStats()
        ]);
        
        setReviews(reviewsData);
        setReviewStats(statsData);
      } catch (error) {
        console.error('Erreur lors du chargement des avis:', error);
      } finally {
        setLoading(false);
      }
    };

    loadReviews();
  }, []);

  const filters = reviewStats ? [
    { id: 'all', name: 'Tous les avis', count: reviewStats.total_reviews },
    { id: '5', name: '5 étoiles', count: reviewStats.rating_distribution[5] || 0 },
    { id: '4', name: '4 étoiles', count: reviewStats.rating_distribution[4] || 0 },
    { id: '3', name: '3 étoiles', count: reviewStats.rating_distribution[3] || 0 },
    { id: '2', name: '2 étoiles', count: reviewStats.rating_distribution[2] || 0 },
    { id: '1', name: '1 étoile', count: reviewStats.rating_distribution[1] || 0 },
  ] : [
    { id: 'all', name: 'Tous les avis', count: 0 },
    { id: '5', name: '5 étoiles', count: 0 },
    { id: '4', name: '4 étoiles', count: 0 },
    { id: '3', name: '3 étoiles', count: 0 },
    { id: '2', name: '2 étoiles', count: 0 },
    { id: '1', name: '1 étoile', count: 0 },
  ];

  const modelFilters = reviewStats ? [
    { id: 'all', name: 'Tous les modèles', count: reviewStats.total_reviews },
    { id: 'iPhone 15 Pro Max', name: 'iPhone 15 Pro Max', count: reviewStats.model_distribution['iPhone 15 Pro Max'] || 0 },
    { id: 'iPhone 15 Pro', name: 'iPhone 15 Pro', count: reviewStats.model_distribution['iPhone 15 Pro'] || 0 },
    { id: 'iPhone 15 Plus', name: 'iPhone 15 Plus', count: reviewStats.model_distribution['iPhone 15 Plus'] || 0 },
    { id: 'iPhone 15', name: 'iPhone 15', count: reviewStats.model_distribution['iPhone 15'] || 0 },
  ] : [
    { id: 'all', name: 'Tous les modèles', count: 0 },
    { id: 'iPhone 15 Pro Max', name: 'iPhone 15 Pro Max', count: 0 },
    { id: 'iPhone 15 Pro', name: 'iPhone 15 Pro', count: 0 },
    { id: 'iPhone 15 Plus', name: 'iPhone 15 Plus', count: 0 },
    { id: 'iPhone 15', name: 'iPhone 15', count: 0 },
  ];

  // Clone des reviews pour gérer les helpful counts
  const [reviewData, setReviewData] = useState([]);

  const filteredReviews = reviewData.filter(review => {
    const matchesRating = selectedFilter === 'all' || review.rating === parseInt(selectedFilter);
    const matchesModel = selectedModel === 'all' || review.model === selectedModel;
    return matchesRating && matchesModel;
  });

  // Mettre à jour reviewData quand les reviews changent
  useEffect(() => {
    setReviewData(reviews.map(review => ({ ...review })));
  }, [reviews]);

  const handleReply = (reviewId) => {
    setReplyingTo(replyingTo === reviewId ? null : reviewId);
    setReplyText('');
  };

  const handleSubmitReply = (reviewId) => {
    if (replyText.trim()) {
      const newReply = {
        id: Date.now(),
        author: 'Utilisateur',
        date: 'À l\'instant',
        content: replyText.trim(),
        helpful: 0
      };
      
      setReplies(prev => ({
        ...prev,
        [reviewId]: [...(prev[reviewId] || []), newReply]
      }));
      
      setReplyText('');
      setReplyingTo(null);
    }
  };

  const handleLikeReview = async (reviewId) => {
    const newLikedReviews = new Set(likedReviews);
    const isLiked = newLikedReviews.has(reviewId);
    
    try {
      // Appeler l'API pour liker/unliker
      await reviewService.likeReview(reviewId);
      
      // Mettre à jour l'état local
      if (isLiked) {
        newLikedReviews.delete(reviewId);
      } else {
        newLikedReviews.add(reviewId);
      }
      
      setLikedReviews(newLikedReviews);
      
      // Update helpful count
      setReviewData(prev => prev.map(review => 
        review.id === reviewId 
          ? { ...review, helpful: review.helpful + (isLiked ? -1 : 1) }
          : review
      ));
    } catch (error) {
      console.error('Erreur lors du like de l\'avis:', error);
    }
  };

  const handleLikeReply = (reviewId, replyId) => {
    const replyKey = `${reviewId}-${replyId}`;
    const newLikedReplies = new Set(likedReplies);
    const isLiked = newLikedReplies.has(replyKey);
    
    if (isLiked) {
      newLikedReplies.delete(replyKey);
    } else {
      newLikedReplies.add(replyKey);
    }
    
    setLikedReplies(newLikedReplies);
    
    // Update reply helpful count
    setReplies(prev => ({
      ...prev,
      [reviewId]: (prev[reviewId] || []).map(reply => 
        reply.id === replyId 
          ? { ...reply, helpful: reply.helpful + (isLiked ? -1 : 1) }
          : reply
      )
    }));
  };

  return (
    <Section id="reviews">
      <Container>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Header>
            <h2>Avis clients</h2>
            <p>
              Découvrez ce que nos clients pensent de leur expérience avec l'iPhone 16 Pro.
            </p>
          </Header>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <StatsContainer>
            <div className="stat-card">
              <div className="rating-display">
                <div className="stars">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="star filled" />
                  ))}
                </div>
                <div className="rating-number">4.8</div>
              </div>
              <div className="total-reviews">Basé sur 1,247 avis</div>
            </div>
            <div className="stat-card">
              <div className="rating-display">
                <div className="rating-number">89%</div>
              </div>
              <div className="total-reviews">Clients satisfaits</div>
            </div>
            <div className="stat-card">
              <div className="rating-display">
                <div className="rating-number">24h</div>
              </div>
              <div className="total-reviews">Réponse moyenne</div>
            </div>
          </StatsContainer>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <FilterContainer>
            <div className="filter-buttons">
              {filters.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => setSelectedFilter(filter.id)}
                  className={`filter-btn ${selectedFilter === filter.id ? 'active' : ''}`}
                >
                  {filter.name}
                  <span className="count">{filter.count}</span>
                </button>
              ))}
            </div>
            <div className="model-filters">
              <h4>Filtrer par modèle:</h4>
              <div className="filter-buttons">
                {modelFilters.map(filter => (
                  <button
                    key={filter.id}
                    onClick={() => setSelectedModel(filter.id)}
                    className={`filter-btn ${selectedModel === filter.id ? 'active' : ''}`}
                  >
                    {filter.name}
                    <span className="count">{filter.count}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="sort-dropdown">
              <select>
                <option>Trier par: Pertinence</option>
                <option>Récents</option>
                <option>Note la plus haute</option>
                <option>Note la plus basse</option>
              </select>
            </div>
          </FilterContainer>
        </motion.div>

        {/* Reviews Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <div style={{ fontSize: '1.2rem', color: '#6b7280', marginBottom: '1rem' }}>
                Chargement des avis...
              </div>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                border: '4px solid #e5e7eb',
                borderTop: `4px solid ${INZA_COLORS.primary}`,
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto'
              }}></div>
            </div>
          ) : (
            <ReviewsGrid>
            {filteredReviews.slice(0, showMore ? filteredReviews.length : 3).map((review, index) => (
              <motion.div
                key={review.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <ReviewCard>
                  <div className="review-header">
                    <div className="author-info">
                      <div className="avatar">{review.avatar}</div>
                      <div className="author-details">
                        <div className="author-name">
                          {review.author}
                          {review.verified && (
                            <CheckCircle style={{ width: '14px', height: '14px', color: '#10b981' }} />
                          )}
                        </div>
                        <div className="review-date">{review.date}</div>
                      </div>
                    </div>
                    <div className="rating">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`star ${star <= review.rating ? 'filled' : 'empty'}`}
                          style={{ width: '16px', height: '16px' }}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="review-content">
                    <h3 className="review-title">{review.title}</h3>
                    <p className="review-text">
                      {review.content}
                    </p>
                  </div>

                  <div className="review-footer">
                    <button 
                      className={`helpful-btn ${likedReviews.has(review.id) ? 'liked' : ''}`}
                      onClick={() => handleLikeReview(review.id)}
                    >
                      <ThumbsUp style={{ 
                        width: '14px', 
                        height: '14px',
                        fill: likedReviews.has(review.id) ? 'white' : 'none'
                      }} />
                      <span>Utile ({review.helpful})</span>
                    </button>
                    <button className="reply-btn" onClick={() => handleReply(review.id)}>
                      <MessageCircle style={{ width: '14px', height: '14px' }} />
                      <span>Répondre</span>
                    </button>
                  </div>

                  {/* Reply Form */}
                  {replyingTo === review.id && (
                    <ReplyForm>
                      <div className="reply-header">
                        <div className="reply-title">Répondre à {review.author}</div>
                        <button className="cancel-btn" onClick={() => setReplyingTo(null)}>
                          <X style={{ width: '16px', height: '16px' }} />
                        </button>
                      </div>
                      <textarea
                        className="reply-textarea"
                        placeholder="Écrivez votre réponse..."
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        maxLength={500}
                      />
                      <div className="reply-actions">
                        <div className="char-count">{replyText.length}/500</div>
                        <button 
                          className="submit-btn"
                          onClick={() => handleSubmitReply(review.id)}
                          disabled={!replyText.trim()}
                        >
                          Publier la réponse
                        </button>
                      </div>
                    </ReplyForm>
                  )}

                  {/* Display Replies */}
                  {(replies[review.id] || []).map(reply => (
                    <div key={reply.id} style={{
                      marginTop: '1rem',
                      padding: '1rem',
                      background: '#f1f5f9',
                      borderRadius: '0.5rem',
                      border: '1px solid #e2e8f0'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <span style={{ fontWeight: '600', fontSize: '0.875rem', color: '#1f2937' }}>
                          {reply.author}
                        </span>
                        <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          {reply.date}
                        </span>
                      </div>
                      <p style={{ fontSize: '0.875rem', color: '#374151', lineHeight: '1.5', marginBottom: '0.5rem' }}>
                        {reply.content}
                      </p>
                      <button 
                      className={`helpful-btn ${likedReplies.has(`${review.id}-${reply.id}`) ? 'liked' : ''}`}
                      style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                      onClick={() => handleLikeReply(review.id, reply.id)}
                    >
                      <ThumbsUp style={{ 
                        width: '12px', 
                        height: '12px',
                        fill: likedReplies.has(`${review.id}-${reply.id}`) ? 'white' : 'none'
                      }} />
                      <span>Utile ({reply.helpful})</span>
                    </button>
                    </div>
                  ))}

                  {review.response && (
                    <div style={{
                      marginTop: '1rem',
                      padding: '1rem',
                      background: '#f8fafc',
                      borderRadius: '0.5rem',
                      border: '1px solid #e2e8f0'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <span style={{ fontWeight: '600', fontSize: '0.875rem', color: '#3b82f6' }}>
                          {review.response.author}
                        </span>
                        <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          {review.response.date}
                        </span>
                      </div>
                      <p style={{ fontSize: '0.875rem', color: '#4b5563', lineHeight: '1.5' }}>
                        {review.response.content}
                      </p>
                    </div>
                  )}
                </ReviewCard>
              </motion.div>
            ))}
            </ReviewsGrid>
          )}
        </motion.div>

        {/* Load More */}
        {filteredReviews.length > 3 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <ShowMoreButton onClick={() => setShowMore(!showMore)}>
              {showMore ? (
                <>
                  <ChevronDown style={{ transform: 'rotate(180deg)' }} />
                  Voir moins d'avis
                </>
              ) : (
                <>
                  <Plus />
                  Voir plus d'avis ({filteredReviews.length - 3} autres)
                </>
              )}
            </ShowMoreButton>
          </motion.div>
        )}
      </Container>
    </Section>
  );
};

export default ReviewsSection;
